import React from 'react';
import Icon, {Icons} from '@pb/kit-component-icon';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './slide-arrows.module.styl';

const classNames = withCssModule(styles);

const HORIZONTAL_DIRECTIONS = ['LEFT', 'RIGHT'] as const;
const VERTICAL_DIRECTIONS = ['TOP', 'BOTTOM'] as const;

const arrowIconsMap = {
    vertical: {
        previous: Icons.ANGLE_UP,
        next: Icons.ANGLE_DOWN,
    },
    horizontal: {
        previous: Icons.ANGLE_LEFT,
        next: Icons.ANGLE_RIGHT,
    },
};

export interface SlideArrowsProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onClick'> {
    /**
     * When `true`, renders top/bottom arrows using `ANGLE_UP` / `ANGLE_DOWN`
     * icons. When `false` (default), renders left/right arrows.
     */
    isVertical?: boolean,
    /**
     * When `true`, the "previous" arrow (left or top) is hidden because the
     * scroll container is already at its start boundary.
     */
    isAtScrollStart: boolean,
    /**
     * When `true`, the "next" arrow (right or bottom) is hidden because the
     * scroll container is already at its end boundary.
     */
    isAtScrollEnd: boolean,
    /**
     * Called when an arrow is clicked. Receives `true` if the user clicked the
     * "previous" (left / top) arrow, or `false` for the "next" (right / bottom)
     * arrow. The parent is responsible for computing and applying the new scroll
     * position.
     */
    onClick: (isLeftDirection: boolean) => void,
}

/**
 * Pair of directional arrow overlays that appear at the edges of a scrollable
 * container when content overflows. Each arrow is conditionally rendered based
 * on `isAtScrollStart` and `isAtScrollEnd`. Clicking an arrow calls `onClick`
 * with a boolean indicating direction so the parent can scroll the container.
 *
 * Used internally by `<Tabs>`, `<ToolsRibbon>`, and similar scrollable list
 * components. Renders `aria-hidden="true"` elements — keyboard scrolling is
 * handled separately by arrow-key navigation hooks.
 */
export const SlideArrows = (
    {
        isVertical = false,
        isAtScrollStart,
        isAtScrollEnd,
        onClick,
        className,
        ...otherProps
    }: SlideArrowsProps) => {
    const scrollDirections = isVertical ? VERTICAL_DIRECTIONS : HORIZONTAL_DIRECTIONS;

    return (
        <>
            {scrollDirections.map(direction => {
                const isLeftDirection = direction === 'LEFT' || direction === 'TOP';
                const shouldShow = isLeftDirection ? !isAtScrollStart : !isAtScrollEnd;

                if (!shouldShow) {
                    return null;
                }

                const arrowIcons = arrowIconsMap[isVertical ? 'vertical' : 'horizontal'][isLeftDirection ? 'previous' : 'next'];

                const slideArrowClasses = classNames(
                    'slide-arrow',
                    `slide-arrow_${direction.toLowerCase()}`,
                    className,
                    {
                        'slide-arrow_vertical': isVertical,
                    },
                );

                return (
                    <div
                        {...otherProps}
                        key={direction}
                        aria-hidden="true"
                        className={slideArrowClasses}
                        onClick={() => onClick(isLeftDirection)}>
                        <Icon size="Size_19" type={arrowIcons}/>
                    </div>
                );
            })}
        </>
    );
};

SlideArrows.displayName = 'SlideArrows';
